import api from './api';

/** "Ogni N minuti" o "alle HH:MM" (ora italiana), eventualmente solo in
 *  alcuni giorni (0 = lunedì). */
export type Frequenza =
  | { tipo: 'ogni'; minuti: number }
  | { tipo: 'alle'; ora: string; giorni?: number[] };

export type StatoAggiornamento = 'inattivo' | 'in_corso' | 'in_coda' | 'in_attesa_turno' | 'disabilitato';

export type Esito = 'ok' | 'partial' | 'error' | 'stopped' | 'interrupted' | 'running' | 'invariato';

export interface AvviatoDa {
  tipo: 'pianificazione' | 'manuale' | 'scrittura' | 'apertura' | 'diretto' | 'webhook';
  utente?: string;
  dettaglio?: string;
}

export interface Avanzamento {
  current: number;
  total: number;
  current_item?: string | null;
}

export interface UltimaEsecuzione {
  at: string;
  esito: Esito;
  righe: number | null;
  durata: number | null;
  errore: string | null;
}

export interface Aggiornamento {
  id: string;
  nome: string;
  gruppo: string;
  origine: string;
  descrizione: string;
  attivo: boolean;
  frequenza: Frequenza;
  completa: { tipo: 'alle'; ora: string; giorni?: number[] } | null;
  prossima_esecuzione: string | null;
  prossima_completa: boolean;
  stato: StatoAggiornamento;
  /** L'Aggiornamento diretto dello stesso dato (es. `ordine` per Ordini), se c'è. */
  diretto: string | null;
  lettura_completa_in_corso: boolean;
  avanzamento: Avanzamento | null;
  ultima: UltimaEsecuzione | null;
}

export interface ElencoAggiornamenti {
  gruppi: string[];
  aggiornamenti: Aggiornamento[];
  indicatori: {
    attivi: number;
    in_esecuzione: number;
    in_coda: number;
    errori_24h: number;
    diretti_oggi: number;
  };
  avvisi: { id: string; nome: string; errore: string | null }[];
}

export interface Esecuzione {
  at: string;
  fine: string | null;
  tipo: 'pianificato' | 'diretto';
  avviato_da: AvviatoDa;
  chiavi: string[];
  completa: boolean;
  esito: Esito;
  righe: number | null;
  durata: number | null;
  errore: string | null;
}

/** Esito di un Aggiornamento diretto: ``invariato`` se il gestionale non ha
 *  reso il dato (non si modifica nulla), ``busy`` se è già in corso. */
export interface RispostaDiretto {
  numero?: string;
  conto?: number;
  /** Conto: ordini letti, Ordini web riletti, Caricamento dei Documenti dell'anno ancora in corso. */
  ordini?: number;
  ordini_web?: number;
  documenti?: boolean | null;
  codici?: string[];
  trovati?: string[];
  mancanti?: string[];
  /** Le tabelle avviate in background (Testi web, OEM, Kit, Giacenze). */
  tabelle?: Record<string, string>;
  /** Articoli: quando è partita la richiesta, per attendere le tabelle. */
  avviatoAlle?: string;
  esito: 'ok' | 'invariato' | 'error' | 'busy';
  errore?: string | null;
  stato?: string | null;
}

export interface StatoTabelleArticolo {
  /** Esito per tabella, `null` finché non ha finito. */
  tabelle: Record<string, Esito | null>;
  finite: boolean;
}

export type RispostaEsegui = {
  status: 'triggered' | 'busy' | 'busy_elsewhere' | 'in_attesa_turno' | 'riavvio' | 'dopo_completa';
  job_id: string;
};

export const aggiornamentiService = {
  elenco: async (): Promise<ElencoAggiornamenti> => {
    const { data } = await api.get<ElencoAggiornamenti>('/api/erp/aggiornamenti');
    return data;
  },

  modifica: async (id: string, patch: { attivo?: boolean; frequenza?: Frequenza }): Promise<Aggiornamento> => {
    const { data } = await api.put<Aggiornamento>(`/api/erp/aggiornamenti/${id}`, patch);
    return data;
  },

  esegui: async (id: string): Promise<RispostaEsegui> => {
    const { data } = await api.post<RispostaEsegui>(`/api/erp/aggiornamenti/${id}/esegui`);
    return data;
  },

  interrompi: async (id: string): Promise<{ status: 'stopping' | 'not_running' }> => {
    const { data } = await api.post<{ status: 'stopping' | 'not_running' }>(`/api/erp/aggiornamenti/${id}/interrompi`);
    return data;
  },

  direttoOrdine: async (numero: string): Promise<RispostaDiretto> => {
    const { data } = await api.post<RispostaDiretto>('/api/erp/aggiornamenti/diretto/ordine', { numero });
    return data;
  },

  direttoArticoli: async (codici: string[]): Promise<RispostaDiretto> => {
    const { data } = await api.post<RispostaDiretto>('/api/erp/aggiornamenti/diretto/articoli', { codici });
    return data;
  },

  /** Se le tabelle avviate da un Aggiornamento diretto di articoli hanno finito. */
  tabelleArticolo: async (dal: string, tabelle: string[]): Promise<StatoTabelleArticolo> => {
    const { data } = await api.get<StatoTabelleArticolo>('/api/erp/aggiornamenti/diretto/articoli/tabelle', {
      params: { dal, tabelle: tabelle.join(',') },
    });
    return data;
  },

  direttoConto: async (conto: number): Promise<RispostaDiretto> => {
    const { data } = await api.post<RispostaDiretto>('/api/erp/aggiornamenti/diretto/conto', { conto });
    return data;
  },

  storico: async (id: string, limit = 50): Promise<Esecuzione[]> => {
    const { data } = await api.get<{ esecuzioni: Esecuzione[] }>(`/api/erp/aggiornamenti/${id}/storico`, {
      params: { limit },
    });
    return data.esecuzioni;
  },
};
